/**
 * Hooks de sessao para a SPA web.
 *
 * useSession() — leitura tipada do auth-store.
 * useBootstrapOnMount() — chama bootstrap() uma vez na montagem do app
 * e instala listener do BroadcastChannel para sincronizar entre tabs.
 */

import { useEffect, useRef } from 'react';
import type { OnboardingState } from '@proadv/types';
import {
  useAuthStore,
  type AuthStatus,
  type PublicUser,
  type TenantSummary,
  type TenantMembership,
} from './auth-store';
import { configureApiClient } from './api-client';
import { subscribe } from './broadcast';

export interface UseSession {
  status: AuthStatus;
  user: PublicUser | null;
  tenantId: string | null;
  tenant: TenantSummary | null;
  isSuperAdmin: boolean;
  memberships: TenantMembership[];
  onboarding: OnboardingState;
  signOut: (reason?: string) => Promise<void>;
}

export function useSession(): UseSession {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const tenant = useAuthStore((s) => s.tenant);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const memberships = useAuthStore((s) => s.memberships);
  const onboarding = useAuthStore((s) => s.onboarding);
  const signOut = useAuthStore((s) => s.signOut);

  return {
    status,
    user,
    tenantId,
    tenant,
    isSuperAdmin,
    memberships,
    onboarding,
    signOut,
  };
}

/**
 * Conecta o api-client ao auth-store, dispara bootstrap() uma vez e
 * instala o listener do BroadcastChannel. Idempotente por design (StrictMode
 * faz double-mount em dev — guardamos com ref).
 */
export function useBootstrapOnMount(): void {
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // Configura hooks injetaveis do api-client antes de qualquer request.
    configureApiClient({
      getAccessToken: () => useAuthStore.getState().accessToken,
      setAccessToken: (token) => useAuthStore.getState().setAccessToken(token),
      onSessionLost: () => {
        // Sign-out forcado — sem chamar API (refresh ja falhou).
        useAuthStore.getState().hardReset('refresh_failed');
      },
      onSubscriptionBlocked: (reason) => {
        // 402 em qualquer request de dominio — bloqueio pode ter acontecido
        // no meio da sessao. O gate de rota (RequireActiveSubscription) reage
        // ao proximo render assim que este estado mudar.
        useAuthStore.getState().setBillingBlocked(reason);
      },
    });

    // Listener cross-tab: outra aba deslogou → reset local.
    const unsubscribe = subscribe((ev) => {
      if (ev.type === 'sign-out') {
        useAuthStore.getState().hardReset('forced_by_other_tab');
      } else if (ev.type === 'tenant-switched') {
        // Re-popula sessao para refletir o novo tenant.
        void useAuthStore.getState().bootstrap();
      }
    });

    void useAuthStore.getState().bootstrap();

    return () => {
      unsubscribe();
    };
  }, []);
}
